import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    Input,
    OnChanges,
    OnInit,
    Output,
    ViewChild,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { BannersConfig, ImagesConfig } from '@frontend/sports/common/client-config-data-access';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { SportsLinkBannerViewModel } from '@frontend/sports/types/components/banners/models';
import { DeviceService, NativeAppService, WINDOW } from '@frontend/vanilla/core';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';

import { ArcRestrictionService } from '../arc/arc-restriction.service';
import { CCBEventFeature, CCBEventService } from '../common/ccb-event.service';
import { ImageQualityService } from '../common/image-quality.service';
import { BannerTrackingService } from './banner-tracking.service';

const FEATURES = {
    width: '800',
    height: '600',
};
@Component({
    selector: 'ms-casino-banner-link',
    templateUrl: 'casino-banner-link.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgClass, IconCustomComponent],
})
export class CasinoBannerLinkComponent implements OnInit, OnChanges {
    @Input() model: SportsLinkBannerViewModel;
    @Input() position = '';
    @Output() bannerDataLoaded = new EventEmitter<boolean>();
    @HostBinding('class') className = 'banner-link';
    link?: string;
    isDesktop = false;

    @ViewChild('bannerLink') bannerLinkRef: ElementRef;

    get bannerLinkElement(): HTMLVideoElement | undefined {
        return this.bannerLinkRef ? this.bannerLinkRef.nativeElement : undefined;
    }

    hideGamingCrossSellByArc: boolean;
    private readonly _window = inject(WINDOW);
    private ccbEventService = inject(CCBEventService);

    constructor(
        private urlHelper: UrlHelperService,
        private nativeAppService: NativeAppService,
        private trackingService: BannerTrackingService,
        private deviceService: DeviceService,
        private redirectHelper: RedirectHelperService,
        private bannerConfig: BannersConfig,
        private arcRestrictionService: ArcRestrictionService,
        private imagesConfig: ImagesConfig,
        private imageQualityService: ImageQualityService,
    ) {
        this.arcRestrictionService.gamingCrossSellEnabled$.pipe(takeUntilDestroyed()).subscribe((state) => {
            this.hideGamingCrossSellByArc = state;
        });
    }

    ngOnInit(): void {
        if (!this.hideGamingCrossSellByArc) {
            this.trackingService.clearBannerTracking();
            this.link = this.getBannerLink();
            this.isDesktop = !this.deviceService.isMobile;

            if (this.imagesConfig.imageQualityParams.homePage.casinoBanner && this.model.imageUrl)
                this.model.imageUrl = this.imageQualityService.setImageQuality(
                    this.model.imageUrl,
                    this.imagesConfig.imageQualityParams.homePage.casinoBanner,
                );
        }
    }

    ngOnChanges(): void {
        if (!this.hideGamingCrossSellByArc) {
            this.link = this.getBannerLink();
        }
    }

    get showThemeIcon(): boolean {
        return this.model.themeIcon !== undefined && this.model.themeIcon.length > 0;
    }

    private getBannerLink(): string | undefined {
        if (!this.model?.link) {
            this.bannerDataLoaded.emit(false);

            return;
        }

        const href = this.model.link.href;
        if (!href) {
            this.bannerDataLoaded.emit(false);

            return;
        }

        if (href.toLowerCase().indexOf('gamelauncher') !== -1) {
            this.bannerDataLoaded.emit(true);

            return href;
        }

        if (href.startsWith('/')) {
            this.bannerDataLoaded.emit(true);

            return this.urlHelper.getUrl(href);
        }

        this.model.link.target = this.nativeAppService.isNative ? '_self' : this.model.link.target || '_self';

        this.bannerDataLoaded.emit(true);

        return href;
    }

    onBannerClick(): void {
        const isCCBEventSent = this.ccbEventService.sendCCBToNative(this.model.link, CCBEventFeature.Banner);

        if (this.link && this.model.title && !isCCBEventSent) {
            this.trackingService.trackBannerClickCrossSell(this.model.title, this.position, this.link);

            if (!this.isDesktop) {
                this.redirectHelper.goToPage(this.link);
            } else {
                const target = this.bannerConfig.desktopCasinoBannerOpenTarget || '_blank';
                this._window.open(this.link, target, `${FEATURES.width}, ${FEATURES.height}`);
            }
        }
    }
}
